import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tray, Spinner, EnvelopeSimple, Phone, ArrowSquareOut, ChatCircleDots, Printer, ChartLineUp, CalendarCheck } from "@phosphor-icons/react";

const TYPES = {
  demo: { label: "Demo request", icon: CalendarCheck, cls: "bg-emerald-500 text-white" },
  quote: { label: "Label quote", icon: Printer, cls: "bg-sky-600 text-white" },
  question: { label: "Question", icon: ChatCircleDots, cls: "bg-amber-500 text-white" },
  prospect: { label: "Prospect score", icon: ChartLineUp, cls: "bg-violet-600 text-white" },
};
const fmtDate = (d) => d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

export default function AdminLeads() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/leads").then(({ data }) => setData(data)).catch(() => setData({ leads: [], counts: {}, new: 0 })); }, []);
  const rows = useMemo(() => {
    if (!data) return [];
    const s = q.trim().toLowerCase();
    return data.leads.filter((l) => (type === "all" || l.type === type) && (!s || [l.name, l.email, l.company, l.summary].join(" ").toLowerCase().includes(s)));
  }, [data, type, q]);
  if (user.role !== "super_admin") return <div className="p-8 text-zinc-500">Super admin access only.</div>;

  return (
    <div className="space-y-4" data-testid="admin-leads-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight flex items-center gap-2"><Tray size={26} weight="bold" /> Leads</h1>
          <p className="text-zinc-500 text-sm mt-1">{data ? `${data.leads.length} total · ${data.new} new` : "Loading…"} — demo requests, label quotes, website questions and prospect scores, newest first</p>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company…" className="h-10 sm:w-72" data-testid="leads-search" />
      </div>
      <div className="flex flex-wrap gap-2" data-testid="leads-filters">
        {[["all", "All"], ...Object.entries(TYPES).map(([k, v]) => [k, v.label])].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setType(k)} className={`h-9 px-3 rounded-full text-sm font-semibold border transition-colors ${type === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-100"}`} data-testid={`leads-filter-${k}`}>
            {l}{k !== "all" && data?.counts?.[k] != null ? ` (${data.counts[k]})` : ""}
          </button>
        ))}
      </div>
      <div className="rounded-lg border border-zinc-200 bg-white divide-y divide-zinc-100">
        {!data ? <div className="py-16 text-center"><Spinner size={28} className="animate-spin text-emerald-600 mx-auto" /></div>
          : rows.length === 0 ? <div className="py-16 text-center text-zinc-400"><Tray size={32} className="mx-auto mb-2 text-zinc-300" />No leads match.</div>
          : rows.map((l) => {
            const T = TYPES[l.type];
            return (
              <div key={`${l.type}-${l.id}`} className={`p-4 flex flex-col sm:flex-row sm:items-start gap-3 ${l.status !== "new" ? "opacity-70" : ""}`} data-testid={`lead-${l.type}-${l.id}`}>
                <div className="flex items-center gap-2 sm:w-40 shrink-0">
                  <Badge className={`${T.cls} hover:${T.cls} gap-1`}><T.icon size={12} weight="bold" /> {T.label}</Badge>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 text-sm">
                    <span className="font-semibold text-zinc-900">{l.name || "—"}</span>
                    {l.company && <span className="text-zinc-500">· {l.company}</span>}
                    {l.status === "new" ? <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200">New</Badge> : <span className="text-xs text-zinc-400 capitalize">{l.status.replace("_", " ")}</span>}
                  </div>
                  <p className="text-sm text-zinc-700 mt-1 truncate">{l.summary}</p>
                  {l.detail && <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{l.detail}</p>}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs">
                    {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1 text-emerald-700 hover:underline"><EnvelopeSimple size={13} /> {l.email}</a>}
                    {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-1 text-zinc-600 hover:underline"><Phone size={13} /> {l.phone}</a>}
                  </div>
                </div>
                <div className="flex sm:flex-col items-center sm:items-end gap-2 shrink-0 text-xs text-zinc-500">
                  <span className="whitespace-nowrap">{fmtDate(l.created_at)}</span>
                  <Link to={l.manage} className="inline-flex items-center gap-1 font-semibold text-zinc-700 hover:text-zinc-950" data-testid={`lead-manage-${l.type}-${l.id}`}>Manage <ArrowSquareOut size={13} /></Link>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
